import React, { Suspense, useCallback, useMemo, useState } from 'react'
import { Box, CircularProgress } from '@mui/material'
import { KiotListExpandDetailModal } from '@/components/common/kiotListExpand'
import { useI18n } from '@/contexts/I18nContext'
import { useMenuChucNang } from '@/hooks/useMenuChucNang'
import { EntityCodeModalContext, type EntityCodeModalContextValue } from './EntityCodeModalContext'
import { ENTITY_CODE_REGISTRY } from './entityCodeRegistry'
import type { EntityCodeOpenRequest } from './entityCodeTypes'

const loadingNode = (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6, flex: 1 }}>
    <CircularProgress size={32} />
  </Box>
)

/**
 * Click mã liên kết (phiếu / đơn / KH / hàng hóa) ở bất kỳ đâu → modal chi tiết;
 * nút Chỉnh sửa trong chi tiết → đóng chi tiết, mở modal sửa của đúng loại.
 */
export const EntityCodeModalProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { common: commonT } = useI18n()
  const { hasAnyChucNangByCode } = useMenuChucNang()
  const [detail, setDetail] = useState<EntityCodeOpenRequest | null>(null)
  const [edit, setEdit] = useState<EntityCodeOpenRequest | null>(null)

  const openEntityDetail = useCallback((req: EntityCodeOpenRequest) => {
    if (!req.id) return
    setEdit(null)
    setDetail(req)
  }, [])

  const ctxValue = useMemo<EntityCodeModalContextValue>(() => ({ openEntityDetail }), [openEntityDetail])

  const detailCfg = detail ? ENTITY_CODE_REGISTRY[detail.type] : null
  const canEdit = Boolean(
    detailCfg?.renderEdit &&
      detailCfg.editPermissions.length > 0 &&
      hasAnyChucNangByCode([...detailCfg.editPermissions]),
  )

  const openEdit = useCallback(() => {
    if (!detail) return
    setDetail(null)
    setEdit(detail)
  }, [detail])

  const closeEdit = useCallback(() => setEdit(null), [])

  const editCfg = edit ? ENTITY_CODE_REGISTRY[edit.type] : null
  const typeName = detail ? commonT(`entityCode.types.${detail.type}`) : ''
  const title = detail?.code
    ? commonT('entityCode.detailTitleWithCode', { name: typeName, code: detail.code })
    : commonT('entityCode.detailTitle', { name: typeName })

  return (
    <EntityCodeModalContext.Provider value={ctxValue}>
      {children}

      {detail && detailCfg ? (
        <KiotListExpandDetailModal
          open
          onClose={() => setDetail(null)}
          title={title}
          onEdit={canEdit ? openEdit : undefined}
        >
          <Suspense key={`${detail.type}:${detail.id}`} fallback={loadingNode}>
            {detailCfg.renderDetail({
              id: detail.id,
              code: detail.code,
              onEdit: canEdit ? openEdit : undefined,
              onChanged: () => detail.onChanged?.(),
            })}
          </Suspense>
        </KiotListExpandDetailModal>
      ) : null}

      {edit && editCfg?.renderEdit ? (
        <Suspense fallback={null}>
          {editCfg.renderEdit({
            id: edit.id,
            onClose: closeEdit,
            onSaved: () => {
              setEdit(null)
              edit.onChanged?.()
            },
            onChanged: () => edit.onChanged?.(),
          })}
        </Suspense>
      ) : null}
    </EntityCodeModalContext.Provider>
  )
}

export default EntityCodeModalProvider
